import React, { useState } from 'react'
import * as API from '../../utility/api'
import UserRow from './user-row'
import download from '../../lib/downloader'

const BASE_URL = 'admin/customers'
const ADMINS_URL = 'admin/customers/admins'

const EXPORT_COLUMNS = [
  { key: 'first_name', label: 'Etunimi' },
  { key: 'last_name', label: 'Sukunimi' },
  { key: 'iban', label: 'IBAN' },
  { key: 'allow_marketing', label: 'markkinointi' },
  { key: 'email', label: 'email' },
  { key: 'phone_number', label: 'puhelin' }
]

const escapeCsvValue = value => {
  if (value == null) {
    return ''
  }

  let stringValue = String(value)

  // Neutralize spreadsheet formula injection (=, +, -, @, tab, CR),
  // including values with leading whitespace before the formula character.
  if (/^\s*[=+\-@\t\r]/.test(stringValue)) {
    stringValue = `'${stringValue}`
  }

  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }

  return stringValue
}

const usersToCsv = users => {
  const header = EXPORT_COLUMNS.map(column => column.label).join(',')
  const rows = users.map(user =>
    EXPORT_COLUMNS.map(column => escapeCsvValue(user[column.key])).join(',')
  )

  // BOM so Excel opens Finnish characters correctly
  return `\uFEFF${[header, ...rows].join('\n')}`
}

const DOWNLOAD_REVOKE_MS = 60_000
const SECOND_FILE_DOWNLOAD_DELAY_MS = 500

const downloadBlob = (content, filename, type) => {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)

  download(url, filename)
  // Delay revoke so the browser can start reading the blob (Firefox / large files)
  setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_REVOKE_MS)
}

const UsersManagement = () => {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [market, setMarket] = useState(false)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(false)
  const busy = loading || exporting

  const submit = e => {
    e.preventDefault()
    setLoading(true)
    setError(false)

    API.GET(BASE_URL, { name, email, market })
      .then(({ data }) => {
        setLoading(false)
        setUsers(data)
      })
      .catch(({ response }) => {
        setError(response.data.message)
        setLoading(false)
      })
  }

  const generateUserRows = () =>
    users.map(user => <UserRow key={`user-row-${user.Username}`} user={user} />)

  const getAdmins = () => {
    setLoading(true)

    API.GET(ADMINS_URL)
      .then(({ data }) => {
        setLoading(false)
        setUsers(data)
      })
      .catch(e => {
        setLoading(false)
        console.log(e)
      })
  }

  const handleExportUsers = () => {
    setExporting(true)
    setError(false)

    API.GET(BASE_URL + '/export')
      .then(({ data }) => {
        const date = new Date().toISOString().slice(0, 10)
        const baseName = `asiakasrekisteri-${date}`

        downloadBlob(
          usersToCsv(data),
          `${baseName}.csv`,
          'text/csv;charset=utf-8;'
        )

        // Yield so the CSV save can start before JSON.stringify blocks the main thread.
        // Chrome also often keeps only the first save when two downloads start in the same tick.
        window.setTimeout(() => {
          try {
            downloadBlob(
              JSON.stringify(data, null, 2),
              `${baseName}.json`,
              'application/json;charset=utf-8'
            )
          } catch {
            setError('Asiakasrekisterin JSON-vienti epäonnistui')
          } finally {
            setExporting(false)
          }
        }, SECOND_FILE_DOWNLOAD_DELAY_MS)
      })
      .catch(e => {
        setExporting(false)
        setError(e?.response?.data?.message || 'Asiakasrekisterin vienti epäonnistui')
      })
  }

  return (
    <div>
      <h4>Etsi käyttäjä</h4>

      <div className="row">
        <div className="col-sm-6">
          <form onSubmit={submit}>
            <label>
              Email
              <input
                type="text"
                style={{ marginLeft: '5px', width: '300px' }}
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </label>

            <label>
              Sukunimi
              <input
                type="text"
                style={{ marginLeft: '5px', width: '300px' }}
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </label>

            <label>
              Vain markkinoinnin hyväksyneet
              <input
                type="checkbox"
                checked={market}
                onChange={e => setMarket(e.target.checked)}
              />
            </label>

            <button type="submit" disabled={busy}>
              {loading ? 'Haetaan...' : 'Hae'}
            </button>
          </form>
        </div>

        <div
          className="col-sm-6"
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <button onClick={handleExportUsers} disabled={busy}>
            {exporting ? 'Ladataan...' : 'Asiakasrekisteri export'}
          </button>
        </div>
      </div>

      <div className="break-sm" />

      <button onClick={() => getAdmins()} disabled={busy}>
        Hae admin-käyttäjät
      </button>

      <div className="break-lg" />

      {error && <div>{error}</div>}
      {users && !error && <div>{generateUserRows()}</div>}
    </div>
  )
}

export default UsersManagement
